import { forwardRef } from 'react';
import { MapPin, Sparkles } from 'lucide-react';

export const styleConfigs = {
  'Romantic Classic': { bgColor: '#FFF9F5', borderColor: '#D4AF37', accentColor: '#D4AF37', textColor: '#2A211C' },
  'Soft Modern': { bgColor: '#FAFAFA', borderColor: '#9CA3AF', accentColor: '#6B7280', textColor: '#1F2937' },
  'Floral Boho': { bgColor: '#FDF2F8', borderColor: '#EC4899', accentColor: '#DB2777', textColor: '#4A223A' },
  'Minimal Luxe': { bgColor: '#FFFFFF', borderColor: '#1F2937', accentColor: '#111827', textColor: '#111827' },
};

const layoutThemes = {
  'classic-elegant': {
    wrapper: 'rounded-[28px] border',
    heading: 'font-serif tracking-[-0.03em]',
    bodyWidth: 'max-w-[80%]',
    badge: 'uppercase tracking-[0.18em]',
  },
  'editorial-portrait': {
    wrapper: 'rounded-[10px] border-[1.5px]',
    heading: 'font-serif tracking-[-0.05em]',
    bodyWidth: 'max-w-[78%]',
    badge: 'uppercase tracking-[0.22em]',
  },
  'floral-romantic': {
    wrapper: 'rounded-[34px] border',
    heading: 'font-serif italic tracking-[-0.04em]',
    bodyWidth: 'max-w-[84%]',
    badge: 'uppercase tracking-[0.18em]',
  },
  'minimal-modern': {
    wrapper: 'rounded-none border',
    heading: 'font-sans tracking-[-0.04em]',
    bodyWidth: 'max-w-[74%]',
    badge: 'uppercase tracking-[0.28em]',
  },
  'luxury-dark': {
    wrapper: 'rounded-[22px] border',
    heading: 'font-serif tracking-[-0.04em]',
    bodyWidth: 'max-w-[78%]',
    badge: 'uppercase tracking-[0.18em]',
  },
  'african-luxury': {
    wrapper: 'rounded-[30px] border-[1.5px]',
    heading: 'font-serif tracking-[-0.04em]',
    bodyWidth: 'max-w-[80%]',
    badge: 'uppercase tracking-[0.22em]',
  },
};

const sizeConfig = {
  small: {
    frame: 'w-48 h-64 p-3',
    content: 'px-1',
    headline: 'mb-1 text-[8px] tracking-[0.18em]',
    names: 'text-[15px] leading-tight',
    body: 'mt-1.5 max-w-[88%] text-[7px] leading-[1.35]',
    divider: 'mt-2 h-px w-9',
    metaWrap: 'mt-2 space-y-1',
    date: 'text-[10px] leading-tight',
    time: 'text-[8px] leading-tight',
    venueWrap: 'mt-2 gap-1 text-[7px]',
    venueText: 'max-w-[80%] leading-[1.3]',
    dressCode: 'mt-2 text-[8px] tracking-[0.16em]',
    closing: 'mt-2 text-[7px] leading-[1.25]',
    rsvp: 'mt-1 text-[6px] leading-[1.25]',
  },
  medium: {
    frame: 'w-72 h-96 p-4',
    content: 'px-2',
    headline: 'mb-2 text-[9px] tracking-[0.2em]',
    names: 'text-[1.5rem] leading-none',
    body: 'mt-2 max-w-[82%] text-[10px] leading-[1.38]',
    divider: 'mt-3 h-px w-10',
    metaWrap: 'mt-3 space-y-1',
    date: 'text-[12px] leading-tight',
    time: 'text-[10px] leading-tight',
    venueWrap: 'mt-3 gap-1 text-[10px]',
    venueText: 'max-w-[74%] leading-[1.3]',
    dressCode: 'mt-3 text-[9px] tracking-[0.16em]',
    closing: 'mt-3 text-[9px] leading-[1.3]',
    rsvp: 'mt-1 text-[8px] leading-[1.25]',
  },
  large: {
    frame: 'w-80 h-[460px] p-5',
    content: 'px-2',
    headline: 'mb-2 text-[10px] tracking-[0.22em]',
    names: 'text-[1.7rem] leading-none',
    body: 'mt-2 max-w-[80%] text-[11px] leading-[1.36]',
    divider: 'mt-3 h-px w-10',
    metaWrap: 'mt-3 space-y-1',
    date: 'text-[14px] leading-tight',
    time: 'text-[11px] leading-tight',
    venueWrap: 'mt-3 gap-1 text-[11px]',
    venueText: 'max-w-[72%] leading-[1.3]',
    dressCode: 'mt-3 text-[9px] tracking-[0.18em]',
    closing: 'mt-3 text-[9px] leading-[1.28]',
    rsvp: 'mt-1 text-[8px] leading-[1.22]',
  },
  full: {
    frame: 'w-full aspect-[3/4] p-6 md:p-8',
    content: 'px-4',
    headline: 'mb-3 text-[10px] tracking-[0.22em] md:text-xs',
    names: 'text-[2rem] leading-none md:text-[2.4rem]',
    body: 'mt-4 max-w-[82%] text-[12px] leading-[1.6] md:text-[14px]',
    divider: 'mt-5 h-px w-14',
    metaWrap: 'mt-5 space-y-2',
    date: 'text-[16px] leading-tight md:text-[20px]',
    time: 'text-[12px] leading-tight md:text-[14px]',
    venueWrap: 'mt-5 gap-2 text-[12px] md:text-[14px]',
    venueText: 'max-w-[76%]',
    dressCode: 'mt-5 text-[10px] tracking-[0.18em] md:text-[11px]',
    closing: 'mt-5 text-[11px] leading-[1.55] md:text-[13px]',
    rsvp: 'mt-2 text-[10px] leading-[1.45] md:text-[11px]',
  },
  exportFull: {
    frame: 'w-full h-full p-16',
    content: '',
    headline: 'mb-6 text-xl tracking-[0.22em]',
    names: 'text-6xl leading-none',
    body: 'mt-4 max-w-[78%] text-3xl leading-relaxed',
    divider: 'mt-5 h-[2px] w-28',
    metaWrap: 'mt-8 space-y-2',
    date: 'text-4xl leading-tight',
    time: 'text-3xl leading-tight',
    venueWrap: 'mt-5 gap-2 text-3xl',
    venueText: 'max-w-[72%]',
    dressCode: 'mt-5 text-xl tracking-[0.16em]',
    closing: 'mt-5 text-2xl leading-relaxed',
    rsvp: 'mt-2 text-xl leading-relaxed',
  },
};

const NUMBER_WORD_MAP = new Map([
  ['zero', '0'],
  ['one', '1'],
  ['two', '2'],
  ['three', '3'],
  ['four', '4'],
  ['five', '5'],
  ['six', '6'],
  ['seven', '7'],
  ['eight', '8'],
  ['nine', '9'],
  ['ten', '10'],
  ['eleven', '11'],
  ['twelve', '12'],
]);

export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return 'Your Wedding Date';
  const value = new Date(dateStr);
  if (Number.isNaN(value.getTime())) return dateStr;
  return value.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const formatDisplayTime = (timeStr) => {
  if (!timeStr) return '';
  if (/am|pm/i.test(timeStr)) return timeStr;
  const [hours, minutes] = timeStr.split(':');
  const hour = Number.parseInt(hours || '0', 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes || '00'} ${ampm}`;
};

function normalizeNumericLanguage(text) {
  if (!text) return text;

  let normalized = text;

  NUMBER_WORD_MAP.forEach((numberValue, numberWord) => {
    const oclockPattern = new RegExp(`\\b${numberWord}\\s+o['’]?clock\\b`, 'gi');
    normalized = normalized.replace(oclockPattern, `${numberValue}:00`);
  });

  NUMBER_WORD_MAP.forEach((numberValue, numberWord) => {
    const pattern = new RegExp(`\\b${numberWord}\\b`, 'gi');
    normalized = normalized.replace(pattern, (match, offset, sourceText) => {
      const prefix = sourceText.slice(0, offset);
      const isLikelyNumericContext = /\b(?:at|on|by|from|before|after|until|rsvp|respond|starting|begins?|begins at|arrive|ceremony|reception)\s+$/i.test(prefix);
      return isLikelyNumericContext ? numberValue : match;
    });
  });

  return normalized.replace(/\s{2,}/g, ' ').trim();
}

function getInvitationCopy(invitation, design) {
  const paletteSource = design?.palette;
  const styleFallback = styleConfigs[invitation?.style || 'Romantic Classic'] || styleConfigs['Romantic Classic'];
  const layout = design?.layout || toLayoutKey(invitation?.style);

  return {
    headline: normalizeNumericLanguage(design?.headline || "You're Invited"),
    body: normalizeNumericLanguage(design?.body || 'Request the pleasure of your company'),
    closing: normalizeNumericLanguage(design?.closing || 'Dinner and dancing to follow'),
    rsvp: normalizeNumericLanguage(design?.rsvp || 'RSVP to follow'),
    names:
      design?.names ||
      `${invitation?.couple_names?.name1 || invitation?.name1 || 'Partner'} & ${invitation?.couple_names?.name2 || invitation?.name2 || 'Partner'}`,
    date: design?.date || formatDisplayDate(invitation?.date),
    time: design?.time || formatDisplayTime(invitation?.time),
    venue: design?.venue || invitation?.location || 'Venue to be announced',
    dressCode: design?.dressCode || invitation?.dress_code || '',
    tone: design?.tone || invitation?.tone || 'Romantic',
    ornament: design?.ornament || 'floral-border',
    styleTags: design?.styleTags || [],
    layout,
    palette: {
      background: paletteSource?.background || design?.bgColor || styleFallback.bgColor,
      primary: paletteSource?.primary || styleFallback.textColor,
      secondary: paletteSource?.secondary || styleFallback.borderColor,
      accent: paletteSource?.accent || design?.accentColor || styleFallback.accentColor,
      text: paletteSource?.text || styleFallback.textColor,
    },
    fonts: design?.fonts || {
      heading: layout === 'minimal-modern' ? 'Manrope' : 'Cormorant Garamond',
      body: 'Source Sans 3',
    },
  };
}

function toLayoutKey(style) {
  const map = {
    'Romantic Classic': 'classic-elegant',
    'Soft Modern': 'minimal-modern',
    'Floral Boho': 'floral-romantic',
    'Minimal Luxe': 'luxury-dark',
  };
  return map[style] || 'editorial-portrait';
}

export const InvitationPreview = forwardRef(
  ({ invitation, design, size = 'medium', className = '', isCrafting = false, exportFormat = 'pdf' }, ref) => {
    const isExport = size === 'exportFull';
    const isStoryExport = isExport && exportFormat === 'story';

    const preview = getInvitationCopy(invitation, design);
    const theme = layoutThemes[preview.layout] || layoutThemes['editorial-portrait'];
    const config = sizeConfig[size] || sizeConfig.medium;

    return (
      <div
        ref={ref}
        className={`preview-invitation relative flex flex-col overflow-hidden ${theme.wrapper} ${config.frame} ${className}`}
        style={{
          backgroundColor: preview.palette.background,
          borderColor: preview.palette.secondary,
          color: preview.palette.text,
          fontFamily: preview.fonts.body,
        }}
        data-testid="invitation-preview"
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            background:
              preview.layout === 'luxury-dark'
                ? `radial-gradient(circle at top, ${preview.palette.accent}25, transparent 42%)`
                : `radial-gradient(circle at top left, ${preview.palette.accent}12, transparent 36%)`,
          }}
        />

        <div
          className="absolute left-4 right-4 top-4 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${preview.palette.secondary}, transparent)` }}
        />
        <div
          className="absolute bottom-4 left-4 right-4 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${preview.palette.secondary}, transparent)` }}
        />

        <div className={`relative z-10 flex h-full flex-col items-center justify-center text-center ${config.content} ${isStoryExport ? 'px-10 py-20' : ''}`}>
          <p className={`uppercase text-[#8A8A8A] ${config.headline}`}>
            {preview.headline}
          </p>

          <h3
            className={`${theme.heading} ${config.names}`}
            style={{ color: preview.palette.primary, fontFamily: preview.fonts.heading }}
          >
            {preview.names}
          </h3>

          <p className={`text-center ${theme.bodyWidth} ${config.body}`}>
            {preview.body}
          </p>

          <div
            className={config.divider}
            style={{ backgroundColor: preview.palette.accent }}
          />

          <div className={config.metaWrap}>
            <p className={`${config.date} font-serif`} style={{ fontFamily: preview.fonts.heading }}>
              {preview.date}
            </p>
            {preview.time ? (
              <p className={config.time} style={{ color: preview.palette.accent }}>
                {preview.time}
              </p>
            ) : null}
          </div>

          <div className={`flex items-center justify-center ${config.venueWrap}`}>
            <MapPin size={isExport ? 24 : size === 'small' ? 8 : size === 'large' ? 14 : 12} style={{ color: preview.palette.accent }} />
            <p className={config.venueText}>{preview.venue}</p>
          </div>

          {preview.dressCode ? (
            <div
              className={`inline-flex items-center justify-center self-center text-center uppercase ${config.dressCode}`}
              style={{
                color: preview.palette.accent,
              }}
            >
              {preview.dressCode}
            </div>
          ) : null}

          <p className={`${config.closing} text-[#7D756E]`}>
            {preview.closing}
          </p>
          <p className={`${config.rsvp} text-[#9A8E82]`}>
            {preview.rsvp}
          </p>
        </div>

        {isCrafting ? (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/78">
            <Sparkles className="animate-pulse text-[#D4AF37]" size={28} />
            <p className="mt-3 font-serif text-sm">Composing your invitation...</p>
            <div className="mt-3 h-1 w-32 overflow-hidden rounded bg-[#D4AF37]/20">
              <div className="h-full bg-[#D4AF37] animate-[shimmer_2s_ease-in-out_infinite]" />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);

InvitationPreview.displayName = 'InvitationPreview';
